import { useSyncExternalStore } from "react";
import {
  FREE_TRIP_LIMIT,
  FREE_LOCATION_LIMIT,
  FREE_BUDDY_LIMIT,
  TRIAL_DAYS,
  type BillingState,
} from "../../shared/types";

// External store for the signed-in user's billing state. Same pattern as
// auth.ts / storage.ts so the gate can be checked anywhere in the tree.

export { FREE_TRIP_LIMIT, FREE_LOCATION_LIMIT, FREE_BUDDY_LIMIT, TRIAL_DAYS };

const EMPTY: BillingState = {
  plan: "free",
  status: "active",
  current_period_end: null,
  cancel_at_period_end: false,
  purchase_type: null,
  provider: "stub",
  subscription_available: false,
  onetime_available: false,
  trial_ends_at: null,
  in_trial: false,
};

type StoredBilling = BillingState | null; // null = unknown / not loaded

let billing: StoredBilling = null;
const listeners = new Set<() => void>();

function set(next: StoredBilling) {
  billing = next;
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function snapshot(): StoredBilling {
  return billing;
}

export function useBilling(): StoredBilling {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}

// Whether `b` currently grants Pro-level access — a paid plan OR an active
// trial. This is what feature gates should check, not `plan` alone.
export function hasProAccess(b: BillingState | null): boolean {
  return b !== null && (b.plan === "pro" || b.in_trial);
}

// The user's effective tier, for UI ("free" | "trial" | "pro").
export type AccessTier = "free" | "trial" | "pro";
export function accessTier(b: BillingState | null): AccessTier {
  if (b === null) return "free";
  if (b.plan === "pro") return "pro";
  return b.in_trial ? "trial" : "free";
}

export function resetPlan(): void {
  set(null);
}

// Shared billing-copy date helpers (used by both BillingPage and the Account
// plan summary, so the two can't drift). "renews on" / "access until" copy.
export function fmtBillingDate(iso: string | null): string {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

// Whole days remaining until an ISO timestamp (0 if past / missing).
export function daysUntil(iso: string | null): number {
  if (!iso) return 0;
  const ms = Date.parse(iso) - Date.now();
  return Number.isFinite(ms) ? Math.max(0, Math.ceil(ms / 86_400_000)) : 0;
}

async function readError(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { error?: string };
    if (body && typeof body.error === "string") return body.error;
  } catch {
    /* fall through */
  }
  return `Request failed (${res.status}).`;
}

function asBillingState(body: unknown): BillingState {
  const r = (body ?? {}) as Partial<BillingState>;
  return {
    ...EMPTY,
    ...r,
    plan: r.plan === "pro" ? "pro" : "free",
  };
}

// Async import so this module doesn't drag the events pipeline into
// every consumer of hasProAccess/accessTier.
type ScalarFields = Record<string, string | number | boolean | null | undefined>;
function emit(name: string, fields: ScalarFields = {}): void {
  import("./events")
    .then((m) => m.logEvent(name, fields))
    .catch(() => {});
}

export async function refreshBilling(): Promise<void> {
  try {
    const res = await fetch("/api/billing", { credentials: "same-origin" });
    if (!res.ok) {
      emit("billing.refresh", { ok: false, status: res.status });
      return; // signed out / not configured — leave unknown
    }
    const next = asBillingState(await res.json());
    set(next);
    emit("billing.refresh", {
      ok: true,
      plan: next.plan,
      status: next.status,
      in_trial: next.in_trial,
    });
  } catch (err) {
    emit("billing.refresh", {
      ok: false,
      error: err instanceof Error ? err.message : String(err),
    });
  }
}

export interface CheckoutOptions {
  kind: "subscription" | "onetime";
  interval?: "monthly" | "yearly";
}

// Starts a checkout. In live Stripe mode the server returns a URL and we
// redirect to it (Checkout is hosted). In stub mode the server flips the
// plan and returns the updated state; the caller can refresh in place.
export async function startCheckout(opts: CheckoutOptions): Promise<void> {
  emit("billing.checkout_click", {
    kind: opts.kind,
    interval: opts.interval ?? "",
  });
  const t0 = performance.now();
  try {
    const res = await fetch("/api/billing/checkout", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify(opts),
    });
    if (!res.ok) {
      const msg = await readError(res);
      emit("billing.checkout_error", {
        kind: opts.kind,
        status: res.status,
        message: msg,
      });
      throw new Error(msg);
    }
    const body = (await res.json()) as { url?: string } & Partial<BillingState>;
    if (body.url) {
      emit("billing.checkout_redirect", {
        kind: opts.kind,
        has_url: true,
        duration_ms: Math.round(performance.now() - t0),
      });
      window.location.href = body.url;
      return;
    }
    emit("billing.checkout_redirect", {
      kind: opts.kind,
      has_url: false,
      duration_ms: Math.round(performance.now() - t0),
    });
    set(asBillingState(body));
  } catch (err) {
    if (!(err instanceof Error && err.message.startsWith("Request failed"))) {
      emit("billing.checkout_error", {
        kind: opts.kind,
        message: err instanceof Error ? err.message : String(err),
      });
    }
    throw err;
  }
}

export async function openBillingPortal(): Promise<void> {
  emit("billing.portal_open", {});
  const res = await fetch("/api/billing/portal", {
    method: "POST",
    credentials: "same-origin",
  });
  if (!res.ok) {
    const msg = await readError(res);
    emit("billing.portal_error", { status: res.status, message: msg });
    throw new Error(msg);
  }
  const body = (await res.json()) as { url?: string };
  if (body.url) {
    window.location.href = body.url;
  }
}

export async function cancelPlan(): Promise<void> {
  emit("billing.cancel_click", {});
  const res = await fetch("/api/billing/cancel", {
    method: "POST",
    credentials: "same-origin",
  });
  if (!res.ok) {
    const msg = await readError(res);
    emit("billing.cancel_error", { status: res.status, message: msg });
    throw new Error(msg);
  }
  const next = asBillingState(await res.json());
  set(next);
  emit("billing.cancel_ok", {
    plan: next.plan,
    cancel_at_period_end: next.cancel_at_period_end,
  });
}

export async function resumePlan(): Promise<void> {
  emit("billing.resume_click", {});
  const res = await fetch("/api/billing/resume", {
    method: "POST",
    credentials: "same-origin",
  });
  if (!res.ok) {
    const msg = await readError(res);
    emit("billing.resume_error", { status: res.status, message: msg });
    throw new Error(msg);
  }
  const next = asBillingState(await res.json());
  set(next);
  emit("billing.resume_ok", { plan: next.plan });
}
